import { useState } from "react";

import { Drawer, type DrawerBaseProps } from "@/components/Drawer/Drawer";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { usePortfolioAnalyticsProjectFilterDraft } from "@/modules/analytics/PortfolioAnalyticsProjectFilterFields";
import type {
  EventAnalyticsCategory,
  EventAnalyticsEventType,
  EventAnalyticsOrigin,
  EventAnalyticsStatus,
} from "@/queries/analytics/event-reporting-types";
import { useTranslation } from "@/translations";

export interface EventAnalyticsFilterValues {
  origin: EventAnalyticsOrigin;
  category: EventAnalyticsCategory;
  status: EventAnalyticsStatus;
}

type EventAnalyticsFilterDrawerProps = DrawerBaseProps & {
  eventType: EventAnalyticsEventType;
  filter: EventAnalyticsFilterValues;
  showOriginFilter: boolean;
  onUpdate: (filter: EventAnalyticsFilterValues) => void;
};

type FilterOption<T> = {
  value: T;
  label: string;
};

export function EventAnalyticsFilterDrawer({
  isOpened,
  onOpenChange,
  eventType,
  filter,
  showOriginFilter,
  onUpdate,
}: EventAnalyticsFilterDrawerProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Drawer.Root
      title={
        eventType === "announcement"
          ? t("page.analytics-events.filters.announcements-title")
          : t("page.analytics-events.filters.title")
      }
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      data-testid="event-analytics-filter-drawer"
    >
      {isOpened && <FilterForm filter={filter} showOriginFilter={showOriginFilter} onUpdate={onUpdate} />}
    </Drawer.Root>
  );
}

function FilterForm({
  filter,
  showOriginFilter,
  onUpdate,
}: Pick<EventAnalyticsFilterDrawerProps, "filter" | "showOriginFilter" | "onUpdate">): React.ReactNode {
  const { t } = useTranslation();
  const [draftFilter, setDraftFilter] = useState(filter);
  const portfolioProjectFilter = usePortfolioAnalyticsProjectFilterDraft();
  const originOptions: FilterOption<EventAnalyticsOrigin>[] = [
    { value: "all", label: t("page.analytics-events.filters.origin.all") },
    { value: "internal", label: t("page.analytics-events.filters.origin.internal") },
    { value: "external", label: t("page.analytics-events.filters.origin.external") },
  ];
  const categoryOptions: FilterOption<EventAnalyticsCategory>[] = [
    { value: "all", label: t("page.analytics-events.filters.category.all") },
    { value: "social", label: t("page.analytics-events.filters.category.social") },
    { value: "maintenance", label: t("page.analytics-events.filters.category.maintenance") },
    { value: "sport", label: t("page.analytics-events.filters.category.sport") },
    { value: "unknown", label: t("page.analytics-events.filters.category.unknown") },
  ];
  const statusOptions: FilterOption<EventAnalyticsStatus>[] = [
    { value: "all", label: t("page.analytics-events.filters.status.all") },
    { value: "active", label: t("page.analytics-events.filters.status.active") },
    { value: "completed", label: t("page.analytics-events.filters.status.completed") },
  ];
  const selectedOrigin = originOptions.find((option) => option.value === draftFilter.origin)!;
  const selectedCategory = categoryOptions.find((option) => option.value === draftFilter.category)!;
  const selectedStatus = statusOptions.find((option) => option.value === draftFilter.status)!;

  const handleClear = () => {
    const clearedFilter: EventAnalyticsFilterValues = { origin: "all", category: "all", status: "all" };
    setDraftFilter(clearedFilter);
    onUpdate(clearedFilter);
    portfolioProjectFilter.clear();
  };

  return (
    <>
      <Drawer.Body>
        <Stack gap="md">
          {portfolioProjectFilter.fields}
          {showOriginFilter && (
            <FormField label={t("page.analytics-events.filters.origin.label")}>
              <Select
                items={originOptions}
                selected={selectedOrigin}
                onChange={(option) => setDraftFilter((current) => ({ ...current, origin: option.value }))}
                renderOption={(option) => option.label}
                keySelector={(option) => option.value}
              />
            </FormField>
          )}
          <FormField label={t("page.analytics-events.filters.category.label")}>
            <Select
              items={categoryOptions}
              selected={selectedCategory}
              onChange={(option) => setDraftFilter((current) => ({ ...current, category: option.value }))}
              renderOption={(option) => option.label}
              keySelector={(option) => option.value}
            />
          </FormField>
          <FormField label={t("page.analytics-events.filters.status.label")}>
            <Select
              items={statusOptions}
              selected={selectedStatus}
              onChange={(option) => setDraftFilter((current) => ({ ...current, status: option.value }))}
              renderOption={(option) => option.label}
              keySelector={(option) => option.value}
            />
          </FormField>
        </Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.Close>
          <Button variant="subtle" onClick={handleClear}>
            {t("common.action.clear")}
          </Button>
        </Drawer.Close>
        <Drawer.Close>
          <Button
            onClick={() => {
              onUpdate(draftFilter);
              portfolioProjectFilter.apply();
            }}
          >
            {t("common.action.apply")}
          </Button>
        </Drawer.Close>
      </Drawer.Actions>
    </>
  );
}
